/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import jsonp from 'fetch-jsonp';
import { buildStatusPollingUrl } from './UrlService';
import { createLogger } from './LoggerService';

const log = createLogger('LaunchStatusService');

interface LaunchStatus {
  lastStatus: Status;
  launchStatusTimestamp?: string;
}

export enum Status {
  Unknown = 'UNKNOWN',
  Error = 'ERROR',
  Success = 'SUCCESS',
  LaunchedApp = 'LAUNCHED_APP',
  Timeout = 'TIMEOUT',
}

// while polling status from the launcher only the first 32 characters viable to request the launch status related to the token
const lastViableTokenCharForStatusUpdatesFromLauncherService = 32;
const timeoutForJSONPCallback = 20 * 1000;
const errorRetryDelay = 1000;

const getLaunchStatus = (
  token: string,
  lastStatus: LaunchStatus
): Promise<LaunchStatus> => {
  const pollStatus =
    lastStatus.lastStatus === Status.Unknown
      ? undefined
      : lastStatus.lastStatus;
  const abbreviatedToken: string = token.substring(
    0,
    lastViableTokenCharForStatusUpdatesFromLauncherService
  );
  const pollUrl: string = buildStatusPollingUrl(
    abbreviatedToken,
    pollStatus,
    lastStatus.launchStatusTimestamp
  );
  return jsonp(pollUrl, {
    jsonpCallback: 'jsonp_callback',
    timeout: timeoutForJSONPCallback,
  })
    .then((response) => response.json())
    .then((status) => ({
      lastStatus: status.launchStatus,
      launchStatusTimestamp: status.launchStatusTimestamp,
    }))
    .catch((message) => {
      log.warn(message);
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve({ lastStatus: Status.Unknown, launchStatusTimestamp: '' });
        }, errorRetryDelay);
      });
    });
};

export const initiateStatusPolling = (
  token: string,
  updateCallback: (status: Status) => void
): void => {
  startStatusPolling(token, updateCallback, 40, { lastStatus: Status.Unknown });
};

const startStatusPolling = (
  token: string,
  updateCallback: (status: Status) => void,
  maxIterations: number,
  lastStatus: LaunchStatus
): void => {
  if (maxIterations <= 0) {
    updateCallback(Status.Timeout);
    return;
  }

  getLaunchStatus(token, lastStatus).then((launchStatus: LaunchStatus) => {
    if (launchStatus.lastStatus !== Status.Unknown) {
      updateCallback(launchStatus.lastStatus);
    }

    if (
      launchStatus.lastStatus !== Status.Success &&
      launchStatus.lastStatus !== Status.Error
    ) {
      startStatusPolling(
        token,
        updateCallback,
        maxIterations - 1,
        launchStatus
      );
    }
  });
};
